'use client'

import React, { FormEvent, useState } from 'react'
import { Rating, RoundedStar } from '@smastrom/react-rating'
import { Box, Flex, Stack, Text, Textarea } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import { useSelector } from 'react-redux'
import Auth from '@/_components/Auth'
import { CField } from '@/_components/CField'
import {
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from '@/components/ui/dialog'
import { toaster } from '@/components/ui/toaster'
import reviewService from '@/_service/review.service'
import { IRedux, IUser } from '@/_store/types'
import { useCustomLocale } from '@/_hooks/useCustomLocale'
import { useIsDesktop } from '@/_hooks/useBreakpoint'
import BottomSheet from '@/_components/Sheet'
import { containsReviewUrl } from '@/_utils/review-text'

type Props = {
  recipeId: string
}

export default function CRating({ recipeId }: Props) {
  const [rate, setRate] = useState(5)
  const [isSent, setSent] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [isOpenModal, setOpenModal] = useState(false)
  const [isAuthDialog, setIsAuthDialog] = useState(false)
  const [reviewMsg, setReviewMsg] = useState('')

  const t = useTranslations()
  const isDesktop = useIsDesktop()
  const currentLocale = useCustomLocale()
  const { user } = useSelector((state: IRedux) => state.auth)
  const ratingLabels = t.raw('rating_labels') as string[]

  const onSelectRate = (newValue: number) => {
    if (isSent) return
    setRate(newValue)
    if (user) {
      setOpenModal(true)
    } else {
      setIsAuthDialog(true)
    }
  }

  const onClose = () => {
    setOpenModal(false)
    if (!isSent) setRate(5)
  }

  const onSubmitReview = (e?: FormEvent, userData?: IUser) => {
    e?.preventDefault()

    if (containsReviewUrl(reviewMsg)) {
      toaster.warning({
        title: t('review_links_not_allowed'),
      })
      return
    }

    if (!reviewMsg || !rate) return

    setLoading(true)
    const client = userData?.id ? userData : user

    reviewService
      .create({
        client_id: client?.id,
        client_mail: '',
        client_name: client?.name,
        client_phone: client?.phone,
        lang: currentLocale,
        recipe_id: recipeId,
        rating: rate,
        related_subject: 'recipe',
        review_message: reviewMsg,
        type: rate > 3 ? 'like' : 'dislike',
      })
      .then(() => {
        setOpenModal(false)
        setSent(true)
        toaster.success({
          title: t('successfully_sent'),
        })
      })
      .catch(() => {
        toaster.error({
          title: t('error_occured'),
        })
      })
      .finally(() => setLoading(false))
  }

  const formContent = (
    <form
      onSubmit={(e) => {
        onSubmitReview(e)
      }}
    >
      <Box color="text" px={isDesktop ? 0 : 4} pb={isDesktop ? 0 : 4}>
        <Box mb={4}>
          <Text fontSize={18} textAlign="center" fontWeight={700}>
            {t('leave_feedback')}
          </Text>
        </Box>
        <Flex py={4} alignItems="center" justifyContent="space-between">
          <Rating
            style={{ maxWidth: 120 }}
            value={rate}
            itemStyles={{
              itemShapes: RoundedStar,
              activeFillColor: '#FF9910',
              inactiveFillColor: '#E0E8F1',
            }}
            onChange={(value: number) => setRate(value)}
          />
          {rate > 0 && (
            <Text fontSize={12} color="text.muted">
              {ratingLabels[rate - 1]}
            </Text>
          )}
        </Flex>
        <Stack gap={4}>
          <CField label={t('your_review')} bgColor="background">
            <Textarea
              value={reviewMsg}
              rounded={12}
              size="lg"
              bgColor="background"
              pt={6}
              placeholder=""
              className="peer"
              name="review"
              onChange={(e) => setReviewMsg(e.target.value)}
            />
          </CField>
        </Stack>
        <Flex mt={6} justifyContent="center">
          <Button
            type="submit"
            rounded={12}
            size="xl"
            w="100%"
            h="56px"
            bg="primary"
            color="primary.text"
            _hover={{ bg: 'primary.hover' }}
            loading={isLoading}
            disabled={!rate || !reviewMsg}
          >
            {t('send')}
          </Button>
        </Flex>
      </Box>
    </form>
  )

  return (
    <>
      <Rating
        readOnly={isSent}
        style={{ maxWidth: 120 }}
        value={rate}
        itemStyles={{
          itemShapes: RoundedStar,
          activeFillColor: 'var(--rate-color, #FFC500)',
          inactiveFillColor: 'var(--gray-color-400, #E0E8F1)',
        }}
        onChange={onSelectRate}
      />

      {isDesktop ? (
        <DialogRoot
          placement="center"
          lazyMount
          open={isOpenModal}
          onOpenChange={(details) => {
            if (!details.open) onClose()
          }}
        >
          <DialogContent rounded={12} p={6}>
            {formContent}
            <DialogCloseTrigger />
          </DialogContent>
        </DialogRoot>
      ) : (
        <BottomSheet isOpen={isOpenModal} onClose={onClose} zIndex={100}>
          {formContent}
        </BottomSheet>
      )}

      <Auth
        open={isAuthDialog}
        onClose={() => setIsAuthDialog(false)}
        onLastConfirm={() => {
          setIsAuthDialog(false)
          setOpenModal(true)
        }}
      />
    </>
  )
}
